/* File: src/pages/GuestPages/EventPage.js */
import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../components/common/Button';
import { useEvent } from '../../contexts/EventContext';
import './EventPage.css';

/**
 * Page d'affichage de l'événement après sélection de la ville
 */
const EventPage = () => {
  const navigate = useNavigate();
  const { eventDetails, loading, error, selectedCity } = useEvent();
  
  // Vérifier que l'utilisateur a passé la vérification d'âge ET sélectionné une ville
  useEffect(() => {
    const birthYear = localStorage.getItem('birthYear');
    const city = localStorage.getItem('selectedCity');
    
    if (!birthYear) {
      navigate('/');
    } else if (!city) {
      navigate('/city-selection');
    }
  }, [navigate]);
  
  const handleGetPass = () => {
    navigate('/register');
  };
  
  const handleAlreadyRegistered = () => {
    navigate('/retrieve-ticket');
  };
  
  const handleBack = () => {
    navigate('/city-selection');
  };
  
  /**
   * Génère le chemin du visuel basé sur la ville
   * Format: flyer-brightbar-[ville].jpg
   * Exemple: flyer-brightbar-kribi.jpg pour Kribi
   */
  const getEventVisual = () => {
    // Si un visuel est défini dans la base de données, l'utiliser
    if (eventDetails?.visuel) {
      return `./assets/images/${eventDetails.visuel}`;
    }
    
    // Sinon, générer le nom du fichier basé sur la ville
    if (selectedCity || eventDetails?.city) {
      const cityName = (selectedCity || eventDetails.city)
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "") // Supprimer les accents
        .replace(/[^a-z0-9]/g, "-") // Remplacer les caractères spéciaux par des tirets
        .replace(/-+/g, "-") // Remplacer les tirets multiples par un seul
        .replace(/^-|-$/g, ""); // Supprimer les tirets au début et à la fin
      
      return `./assets/images/flyer-brightbar-${cityName}.jpg`;
    }
    
    // Visuel par défaut si aucune ville n'est définie
    return '/assets/images/flyer-brightbar-default.jpg';
  };
  
  // Formater la date de l'événement
  const formatEventDate = (dateString) => {
    if (!dateString) return '';
    
    const date = new Date(dateString);
    const options = { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    };
    
    return date.toLocaleDateString('fr-FR', options)
      .replace(/^\w/, c => c.toUpperCase()); // Première lettre en majuscule
  };
  
  // Afficher un message de chargement
  if (loading) {
    return (
      <div className="event-page loading-page">
        <div className="loading-container">
          <img 
            src="/assets/images/guinness-logo.png" 
            alt="Guinness Cameroun"
            className="loading-logo" 
          />
          <div className="spinner"></div>
          <p>Chargement des informations de l'événement...</p>
        </div>
      </div>
    );
  }
  
  // Afficher un message d'erreur
  if (error && !eventDetails) {
    return (
      <div className="event-page error-page">
        <div className="error-container">
          <img 
            src="/assets/images/guinness-logo.png" 
            alt="Guinness Cameroun"
            className="error-logo" 
          />
          <h2>Erreur</h2>
          <p>{error}</p>
          <Button
            variant="guinness"
            onClick={() => navigate('/city-selection')}
          >
            Retour à la sélection de ville
          </Button>
        </div>
      </div>
    );
  }
  
  // Vérifier que l'événement existe
  if (!eventDetails) {
    return (
      <div className="event-page error-page">
        <div className="error-container">
          <img 
            src="/assets/images/guinness-logo.png" 
            alt="Guinness Cameroun"
            className="error-logo" 
          />
          <h2>Aucun événement disponible</h2>
          <p>L'événement pour {selectedCity} n'est pas disponible actuellement.</p>
          <Button
            variant="guinness"
            onClick={() => navigate('/city-selection')}
          >
            Choisir une autre ville
          </Button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="event-page">
      <div className="event-container">
        <div className="back-arrow" onClick={handleBack}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M19 12H5" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M12 19L5 12L12 5" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
        
        <div className="event-logo">
          <img src="/assets/images/guinness-logo.png" alt="Guinness Cameroun" />
        </div>
        
        <div className="event-content">
          {/* Badge de la ville */}
          {(selectedCity || eventDetails?.city) && (
            <div className="event-city-badge">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" stroke="currentColor" strokeWidth="2"/>
                <circle cx="12" cy="10" r="3" stroke="currentColor" strokeWidth="2"/>
              </svg>
              {selectedCity || eventDetails.city}
            </div>
          )}
          
          {/* Flyer de l'événement */}
          <div className="event-flyer">
            <img 
              src={getEventVisual()} 
              alt={`Guinness Bright Bar ${selectedCity || eventDetails?.city || ''}`}
              onError={(e) => {
                // Si l'image n'existe pas, utiliser une image par défaut
                e.target.onerror = null;
                e.target.src = '/assets/images/flyer-brightbar-default.jpg';
              }}
            />
          </div>
          
          {/* Informations de l'événement */}
          <div className="event-info">
            <h2>{eventDetails.event_name || eventDetails.name}</h2>
            <p className="event-date">
              📅 {formatEventDate(eventDetails.event_date || eventDetails.date)}
            </p>
            <p className="event-location">
              📍 {eventDetails.location}
            </p>
            {eventDetails.description && (
              <p className="event-description">{eventDetails.description}</p>
            )}
          </div>
          
          {/* Actions */}
          <div className="event-actions">
            <Button
              variant="guinness"
              size="lg"
              fullWidth
              onClick={handleGetPass}
            >
              OBTENIR MON PASS
            </Button>
            
            <div className="retrieve-ticket-link">
              <button 
                className="text-button"
                onClick={handleAlreadyRegistered}
              >
                Déjà inscrit ? Retrouver mon pass
              </button>
            </div>
          </div>
        </div>
        
        <div className="event-footer">
          <p>
            La consommation d'alcool est réservée aux personnes âgées de plus de 21 ans. 
            L'abus d'alcool est dangereux pour la santé, à consommer avec modération.
          </p>
        </div>
      </div>
    </div>
  );
};

export default EventPage;